/** @jsxImportSource @emotion/react */
import * as React from "react";
import { useEffect } from "react";
import styled from "@emotion/styled";
import { Global, Theme } from "@emotion/react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  FadeIn,
  H1,
  H2,
  IconArrow,
  SiteContainer,
  Sparkles,
  Text,
} from "atoms";
import { themePrototype } from "styles/themePrototype";
import sadFaceSVG from "assets/images/sad-face.svg";
import { Head } from "features/misc";
import { useProfilePoints, useUpdateProfile } from "features/profile";
import { RedirectState } from "types";
import { useAuth } from "features/auth";
import { constants } from "config";

function BecomeAModerator() {
  // Check auth context for user's reviewer status
  const { user } = useAuth();
  const isReviewer = !!user?.roles.includes("curator-level-1");
  const isModerator = !!user?.roles.includes("curator-level-2");
  const { data: profilePoints } = useProfilePoints();

  const isEligible =
    (profilePoints?.lifetimeEarnings ?? 0) >= constants.pointsRequiredLevel2;

  const navigate = useNavigate();
  // const location = useLocation();
  // const previousLocation = (location.state as RedirectState)?.from;

  const { updateProfile } = useUpdateProfile();

  // Update the user's profile which should refresh the Auth context, see above
  const completeReviewerTraining = () => {
    updateProfile(
      {
        ...user,
        completedLevel2Training: true,
      },
      {
        onSuccess: () => {
          // If redirected here, return to original location
          // if (previousLocation) navigate(previousLocation);
          Complete();
        },
      }
    );
  };

  if (isModerator) {
    // Once the user successfully completes the quiz, they get redirected to the write review form
    // they originally tried to visit before ending up here. But in case the user is already a
    // reviewer and returns to this page, show this message.
    return (
      <Wrapper>
        <Complete />
      </Wrapper>
    );
  }

  if (!isEligible) {
    return (
      <Wrapper
        css={{
          backgroundColor: themePrototype.colors.yellow,
          textAlign: "center",
        }}
      >
        <Header>
          <img
            src={sadFaceSVG}
            width="100"
            height="100"
            css={{
              borderRadius: themePrototype.borderRadius.round,
              boxShadow:
                "0 0 50px 0 rgba(255,255,255,.5), 0 0 20px 0 rgba(255,255,255,.33), 0 0 5px 0 rgba(255,255,255,.33)",
            }}
            alt="Sad face with puppy eyes illustration"
          />
          <H2>
            Sorry, you are not eligible to become&nbsp;a&nbsp;Jr. Curator.
          </H2>
        </Header>

        <Text size="md">
          You have earned {profilePoints?.lifetimeEarnings} points but you need{" "}
          {constants.pointsRequiredLevel2} to be eligible.
        </Text>

        <Button variant="gradient" onClick={() => navigate(-1)} arrow={null}>
          <IconArrow dir="ul" />
          &nbsp;&nbsp;&nbsp;Go Back
        </Button>
      </Wrapper>
    );
  }

  if (!isReviewer) {
    // Once the user successfully completes the quiz, they get redirected to the write review form
    // they originally tried to visit before ending up here. But in case the user is already a
    // reviewer and returns to this page, show this message.
    return (
      <Wrapper
        css={{
          backgroundColor: themePrototype.colors.yellow,
          textAlign: "center",
        }}
      >
        <Header>
          <img
            src={sadFaceSVG}
            width="100"
            height="100"
            css={{
              borderRadius: 100,
              boxShadow:
                "0 0 50px 0 rgba(255,255,255,.5), 0 0 20px 0 rgba(255,255,255,.33), 0 0 5px 0 rgba(255,255,255,.33)",
            }}
            alt="Sad face with puppy eyes illustration"
          />
          <H2>You must become a reviewer before you can become a moderator.</H2>
        </Header>

        <Button variant="gradient" onClick={() => navigate(-1)} arrow={null}>
          <IconArrow dir="ul" />
          &nbsp;&nbsp;&nbsp;Go Back
        </Button>
      </Wrapper>
    );
  }

  return (
    <Wrapper
      css={{
        backgroundColor: themePrototype.colors.yellow,
        textAlign: "center",
      }}
    >
      <Header>
        <H1>
          Become a Jr. Curator
          <Sparkles aria-hidden="true" className="Sparkles" />
        </H1>
      </Header>

      <H2 as="p" size="lg">
        <strong>Thank you for being a part of HUG!</strong>
      </H2>
      <Text>
        You have earned the official Jr. Curator role, where you can write
        moderate reviews from other HUGGERS and earn HUG Points.
      </Text>

      <Text>
        <strong>Click to Accept your new role</strong>
      </Text>

      <Button onClick={completeReviewerTraining} variant="gradient">
        Accept
      </Button>
    </Wrapper>
  );
}

function Wrapper({ children, ...props }: { children: React.ReactNode }) {
  return (
    <Main {...props}>
      <Head title="Become a Jr. Curator" />

      <Global styles={global} />

      <SiteContainer>{children}</SiteContainer>
    </Main>
  );
}

function Complete() {
  const navigate = useNavigate();
  const location = useLocation();
  const previousLocation = (location.state as RedirectState)?.from;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <FadeIn css={{ textAlign: "center" }}>
      <Header>
        <H2 size="h1">
          Congratulations!
          <Sparkles aria-hidden="true" className="Sparkles" />
        </H2>
      </Header>

      <Text size="md">
        <strong>
          You have successfully completed our onboarding
          <br /> and are officially a Hugger!
        </strong>
      </Text>
      <Text>
        You have been rewarded with 250 HUGs and can earn further
        by&nbsp;reviewing&nbsp;projects and artists, and moderating reviews from
        other Huggers!
      </Text>

      {previousLocation ? (
        <Button variant="gradient" onClick={() => navigate(previousLocation)}>
          Continue to Project Review
        </Button>
      ) : (
        <Button variant="gradient" onClick={() => navigate("/moderation/")}>
          Explore Reviews to Moderate
        </Button>
      )}
    </FadeIn>
  );
}

const Main = styled.main(({ theme }) => ({
  background: `radial-gradient(circle farthest-corner at top center, ${theme.colors.pink} 5rem, rgba(255, 255, 255, 0) 30rem)`,
  overflow: "visible",
  "p+button": {
    marginTop: 20,
  },
}));

const Header = styled.header(({ theme }) => ({
  marginLeft: "-1rem",
  marginRight: "-1rem",
  padding: "0 0 3rem",
  position: "relative",
  textAlign: "center",
  "h1, h2": {
    margin: "0 auto",
    maxWidth: 660,
    padding: "0 1em",
    position: "relative",
  },
  ".Sparkles": {
    backgroundSize: "min(60px, 15%)",
    bottom: 0,
    left: "-5%",
    minHeight: 70,
    position: "absolute",
    right: 0,
    top: 0,
  },
}));

const global = (theme: Theme) => ({
  "html,body": {
    // background: theme.colors.yellow,
  },
});

export { BecomeAModerator };
